import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { engagementService } from '../services/engagementService';

const EngagementMetrics = ({ modelId }) => {
  const [metrics, setMetrics] = useState({
    leadsToday: 0,
    completionsToday: 0,
    averageTimeInFunnel: 0
  });
  const [hourlyData, setHourlyData] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!modelId) {
        console.log('No modelId provided');
        return;
      }

      try {
        console.log('Fetching metrics for modelId:', modelId);
        setLoading(true);
        setError(null);
        
        // Buscar estatísticas
        const stats = await engagementService.getFunnelStats(modelId);
        console.log('Stats received:', stats);
        
        if (stats && stats.metrics) {
          setMetrics(stats.metrics);
          setHourlyData(stats.hourlyData || []);
          // Verificar se há dados reais
          setHasData(stats.metrics.leadsToday > 0 || stats.metrics.completionsToday > 0);
        }

        // Buscar histórico
        const historyData = await engagementService.getEngagementHistory(modelId);
        console.log('History received:', historyData);
        
        if (historyData) {
          setHistory(historyData);
        }

      } catch (err) {
        console.error('Erro detalhado ao carregar métricas:', err);
        setError(err.message || 'Erro ao carregar métricas. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Atualizar a cada 5 minutos
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [modelId]);

  if (loading) {
    return (
      <div className="p-4 bg-white rounded-lg shadow">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-white rounded-lg shadow">
        <div className="text-red-500">{error}</div>
        <div className="mt-2 text-sm text-gray-500">
          ModelId: {modelId}
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="p-4 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Métricas de Engajamento</h2>
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">Nenhum dado de engajamento disponível ainda.</div>
          <div className="text-sm text-gray-400">
            Os dados serão exibidos automaticamente quando houver interações com leads.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">Métricas de Engajamento</h2>
      
      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-600">Leads Hoje</h3>
          <p className="text-2xl font-bold text-blue-700">{metrics.leadsToday || 0}</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <h3 className="text-sm font-medium text-green-600">Finalizações Hoje</h3>
          <p className="text-2xl font-bold text-green-700">{metrics.completionsToday || 0}</p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg">
          <h3 className="text-sm font-medium text-purple-600">Tempo Médio no Funil</h3>
          <p className="text-2xl font-bold text-purple-700">{metrics.averageTimeInFunnel || 0} min</p>
        </div>
      </div>

      {/* Gráfico de Atividade por Hora */}
      {hourlyData.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Atividade por Hora</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="leads" stroke="#3B82F6" name="Leads" />
                <Line type="monotone" dataKey="completions" stroke="#10B981" name="Finalizações" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Histórico de Finalizações */}
      {history.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-2">Histórico de Finalizações</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="completions" stroke="#10B981" name="Finalizações" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementMetrics; 